/**
 * Servizio di caching centralizzato per ottimizzare le performance
 */

import { secureLog } from '@/utils/secureLogger';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

class CacheService {
  private cache: Map<string, CacheEntry<any>>;
  private readonly DEFAULT_TTL = 3 * 60 * 1000; // 3 minuti default (ottimizzato per performance)
  private readonly MAX_ENTRIES = 10000; // Limite massimo entry (aumentato per scalabilità)
  private readonly MAX_ENTRY_SIZE = 1024 * 1024; // 1MB per entry

  constructor() {
    this.cache = new Map();
  }

  /**
   * Salva dati in cache con TTL personalizzato e limiti di sicurezza
   */
  set<T>(key: string, data: T, ttl?: number): void {
    // Verifica dimensione entry
    const dataSize = JSON.stringify(data).length;
    if (dataSize > this.MAX_ENTRY_SIZE) {
      secureLog.warn('Cache entry too large, ignored:', { size: dataSize });
      return;
    }

    // Se raggiunto il limite, rimuovi le entry più vecchie
    if (this.cache.size >= this.MAX_ENTRIES) {
      this.evictOldest();
    }

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.DEFAULT_TTL,
    };
    this.cache.set(key, entry);
  }

  /**
   * Rimuove le entry più vecchie quando si raggiunge il limite
   */
  private evictOldest(): void {
    const entries = Array.from(this.cache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    // Rimuovi il 10% delle entry più vecchie
    const toRemove = Math.ceil(this.MAX_ENTRIES * 0.1);
    for (let i = 0; i < toRemove && i < entries.length; i++) {
      this.cache.delete(entries[i][0]);
    }
  }

  /**
   * Recupera dati dalla cache se ancora validi
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }

    const now = Date.now();
    const isExpired = now - entry.timestamp > entry.ttl;

    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * Verifica se un dato è in cache e ancora valido
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Rimuove un elemento dalla cache
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Svuota completamente la cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Ottiene o carica dati con caching automatico e stale-while-revalidate
   */
  async getOrFetch<T>(
    key: string,
    fetchFn: () => Promise<T>,
    ttl?: number,
    staleWhileRevalidate: boolean = true
  ): Promise<T> {
    const entry = this.cache.get(key);
    
    if (entry) {
      const now = Date.now();
      const isExpired = now - entry.timestamp > entry.ttl;
      
      if (!isExpired) {
        return entry.data as T;
      }
      
      // Stale-while-revalidate: return stale data immediately, refresh in background
      if (staleWhileRevalidate) {
        // Refresh in background
        fetchFn().then(data => {
          this.set(key, data, ttl);
        }).catch(() => {
          // Silently fail background refresh
        });
        return entry.data as T;
      }
    }

    // Se non in cache o expired senza SWR, esegui fetch
    const data = await fetchFn();
    this.set(key, data, ttl);
    return data;
  }

  /**
   * Prefetch data in background
   */
  prefetch<T>(key: string, fetchFn: () => Promise<T>, ttl?: number): void {
    fetchFn().then(data => {
      this.set(key, data, ttl);
    }).catch(() => {
      // Silently fail prefetch
    });
  }
}

// Esporta un'istanza singleton
export const cacheService = new CacheService();
